import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../../../../hooks/useTenant';

import { useSalesCart } from './useSalesCart';
import { useSalesFinances } from './useSalesFinances';
import { useSalesEnrollment } from './useSalesEnrollment';
import { useSalesProcess } from './useSalesProcess';
import { useSalesInitialization } from './useSalesInitialization';

/**
 * Hook customizado para gerenciar a lógica da página de Ponto de Venda.
 * Combina todos os sub-hooks de carrinho, matrícula e processamento.
 */
export const useSalesPoint = () => {
    const location = useLocation();

    // Configurações e Sessão
    const { idTenant, loading: tenantLoading, user } = useTenant();
    const isReady = !tenantLoading && !!idTenant;

    // 1. Obtenção de contexto (Usuário e Membro)
    const id = location.state?.id;                 // Firestore document ID
    const idMember = location.state?.idMember;     // ID sequencial (exibição)
    const memberName = location.state?.memberName || "Aluno";
    const memberPhone = location.state?.memberPhone || null;
    const memberEmail = location.state?.memberEmail || null;
    const memberPhotoUrl = location.state?.memberPhotoUrl || null;

    const idConsultant = location.state?.idConsultant || null;
    const consultantName = location.state?.consultantName || null;
    const consultantEmail = location.state?.consultantEmail || null;
    const consultantPhone = location.state?.consultantPhone || null;
    const consultantPhotoUrl = location.state?.consultantPhotoUrl || null;
    const hasMemberContext = Boolean(id);

    // 2. Estados Globais Livres
    const [activeTab, setActiveTab] = useState('1');
    const {
        acquirers,
        memberships,
        currentShift,
        isLoadingData,
        isCashierModalOpen,
        isProcessingShift,
        handleOpenShift,
        toggleCashierModal
    } = useSalesInitialization({ isReady, idTenant, user });

    const [saleDate, setSaleDate] = useState(() => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split('T')[0];
    });
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split('T')[0];
    });

    // 3. Sub-hooks (Divisão de Responsabilidades)
    const {
        pendingEnrollments,
        setPendingEnrollments,
        showEnrollmentCartId,
        setShowEnrollmentCartId,
        clearEnrollmentsForCartId
    } = useSalesEnrollment();

    const {
        cartItems,
        handleAddItem,
        handleRemoveItem
    } = useSalesCart({
        onMembershipReplaced: clearEnrollmentsForCartId,
        onItemRemoved: clearEnrollmentsForCartId
    });

    const {
        payments,
        handleAddPayment,
        handleRemovePayment,
        discount,
        setDiscount,
        totals
    } = useSalesFinances({ cartItems });

    const {
        isProcessing,
        saleSuccessData,
        showReceiptModal,
        handleCloseReceipt,
        handleFinalizeSale: finalizeHelper
    } = useSalesProcess({
        idTenant,
        user,
        id,
        idMember,
        memberName,
        memberPhone,
        memberEmail,
        memberPhotoUrl,
        idConsultant,
        consultantName,
        consultantEmail,
        consultantPhone,
        consultantPhotoUrl
    });

    // 4. Modais ou efeitos não declarativos delegados ao useSalesInitialization

    // 5. Interface Ações Globais
    const toggleTab = useCallback((tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    }, [activeTab]);

    const handleFinalizeSale = (finalizeData) => {
        // Encapa os argumentos para o subhook de Process
        return finalizeHelper({
            finalizeData,
            saleDate,
            startDate,
            cartItems,
            payments,
            totals,
            pendingEnrollments,
            idCashierShift: currentShift?.id
        });
    };

    return {
        // Status do hook e Contexto do Aluno
        isReady,
        hasMemberContext,
        isLoadingData: isLoadingData || !isReady,
        memberName,

        // Dados puxados
        data: { acquirers, memberships },

        // Estados Globais
        activeTab,
        toggleTab,
        saleDate, setSaleDate,
        startDate, setStartDate,

        // Carrinho
        cartItems,
        handleAddItem,
        handleRemoveItem,

        // Pagamento e Totais
        payments,
        handleAddPayment,
        handleRemovePayment,
        discount, setDiscount,
        totals,

        // Formulário Recibo e Processamento
        isProcessing,
        handleFinalizeSale,
        saleSuccessData,
        showReceiptModal,
        handleCloseReceipt,

        // Matrículas embutidas
        pendingEnrollments, setPendingEnrollments,
        showEnrollmentCartId, setShowEnrollmentCartId,

        // Modal de Abertura de Caixa
        isCashierModalOpen,
        toggleCashierModal,
        isProcessingShift,
        handleOpenShift,
        currentShift
    };
};
